import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Save } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ['Lunedi', 'Martedi', 'Mercoledi', 'Giovedi', 'Venerdi', 'Sabato', 'Domenica'];
const SCHEDULE_TYPES = [
  { value: 'closed', label: 'Chiuso' },
  { value: 'morning_afternoon', label: 'Mattina e Pomeriggio' },
  { value: 'morning_only', label: 'Solo Mattina' },
  { value: 'afternoon_only', label: 'Solo Pomeriggio' },
  { value: 'continuous', label: 'Orario Continuato' },
];

const defaultSchedule = DAYS.reduce((acc, day) => {
  acc[day] = day === 'Lunedi' || day === 'Domenica'
    ? { type: 'closed', morning_open: '', morning_close: '', afternoon_open: '', afternoon_close: '', continuous_open: '', continuous_close: '' }
    : { type: 'morning_afternoon', morning_open: '09:00', morning_close: '13:00', afternoon_open: '15:00', afternoon_close: '19:00', continuous_open: '', continuous_close: '' };
  return acc;
}, {});

const AdminSettingsSection = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [salonInfo, setSalonInfo] = useState({ description: '', address: '', phone: '', instagram: '', map_url: '' });
  const [schedule, setSchedule] = useState(defaultSchedule);
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchSalonInfo(); }, []);

  const fetchSalonInfo = async () => {
    try {
      const response = await axios.get(`${API}/salon-info`);
      setSalonInfo(response.data);
      if (response.data.schedule) {
        setSchedule({ ...defaultSchedule, ...response.data.schedule });
      } else if (response.data.opening_hours) {
        // Migrate old format
        const migrated = { ...defaultSchedule };
        Object.entries(response.data.opening_hours).forEach(([day, hours]) => {
          if (hours === 'Chiuso') {
            migrated[day] = { ...migrated[day], type: 'closed' };
          } else {
            migrated[day] = { ...migrated[day], type: 'continuous', continuous_open: hours.split(' - ')[0] || '09:00', continuous_close: hours.split(' - ')[1] || '19:00' };
          }
        });
        setSchedule(migrated);
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const updateDaySchedule = (day, field, value) => {
    setSchedule(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...salonInfo, schedule };
      // Also build legacy opening_hours for backward compat
      const opening_hours = {};
      DAYS.forEach(day => {
        const s = schedule[day];
        if (s.type === 'closed') opening_hours[day] = 'Chiuso';
        else if (s.type === 'morning_afternoon') opening_hours[day] = `${s.morning_open}-${s.morning_close} / ${s.afternoon_open}-${s.afternoon_close}`;
        else if (s.type === 'morning_only') opening_hours[day] = `${s.morning_open}-${s.morning_close}`;
        else if (s.type === 'afternoon_only') opening_hours[day] = `${s.afternoon_open}-${s.afternoon_close}`;
        else if (s.type === 'continuous') opening_hours[day] = `${s.continuous_open}-${s.continuous_close}`;
      });
      payload.opening_hours = opening_hours;
      await axios.put(`${API}/salon-info`, payload, { headers: getAuthHeaders() });
      toast.success('Salvato!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="settings-section">
      <h2 className="text-xl sm:text-2xl font-bold">Orari & Contatti</h2>

      <Card className="card-elegant">
        <CardHeader className="pb-3"><CardTitle className="text-base">Informazioni</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Descrizione</Label><Textarea value={salonInfo.description} onChange={(e) => setSalonInfo({ ...salonInfo, description: e.target.value })} rows={2} className="text-base" /></div>
          <div><Label>Indirizzo</Label><Input value={salonInfo.address} onChange={(e) => setSalonInfo({ ...salonInfo, address: e.target.value })} className="text-base" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Telefono</Label><Input value={salonInfo.phone} onChange={(e) => setSalonInfo({ ...salonInfo, phone: e.target.value })} className="text-base" /></div>
            <div><Label>Instagram</Label><Input value={salonInfo.instagram} onChange={(e) => setSalonInfo({ ...salonInfo, instagram: e.target.value })} className="text-base" /></div>
          </div>
          <div><Label>URL Mappa</Label><Input value={salonInfo.map_url || ''} onChange={(e) => setSalonInfo({ ...salonInfo, map_url: e.target.value })} className="text-base" /></div>
        </CardContent>
      </Card>

      <Card className="card-elegant">
        <CardHeader className="pb-3"><CardTitle className="text-base">Orari di Apertura</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {DAYS.map(day => {
            const s = schedule[day];
            return (
              <div key={day} className="space-y-2 pb-3 border-b border-border/50 last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-3">
                  <Label className="font-semibold text-sm w-20 flex-shrink-0">{day}</Label>
                  <Select value={s.type} onValueChange={(v) => updateDaySchedule(day, 'type', v)}>
                    <SelectTrigger className="flex-1 text-xs sm:text-sm h-9" data-testid={`schedule-type-${day}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHEDULE_TYPES.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                {s.type === 'morning_afternoon' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-0 sm:pl-[92px]">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground font-medium">Mattina</p>
                      <div className="flex gap-2 items-center">
                        <Input type="time" value={s.morning_open} onChange={(e) => updateDaySchedule(day, 'morning_open', e.target.value)} className="text-sm h-9 flex-1" />
                        <span className="text-xs text-muted-foreground">-</span>
                        <Input type="time" value={s.morning_close} onChange={(e) => updateDaySchedule(day, 'morning_close', e.target.value)} className="text-sm h-9 flex-1" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground font-medium">Pomeriggio</p>
                      <div className="flex gap-2 items-center">
                        <Input type="time" value={s.afternoon_open} onChange={(e) => updateDaySchedule(day, 'afternoon_open', e.target.value)} className="text-sm h-9 flex-1" />
                        <span className="text-xs text-muted-foreground">-</span>
                        <Input type="time" value={s.afternoon_close} onChange={(e) => updateDaySchedule(day, 'afternoon_close', e.target.value)} className="text-sm h-9 flex-1" />
                      </div>
                    </div>
                  </div>
                )}

                {s.type === 'morning_only' && (
                  <div className="pl-0 sm:pl-[92px]">
                    <p className="text-xs text-muted-foreground font-medium mb-1">Mattina</p>
                    <div className="flex gap-1 items-center max-w-xs">
                      <Input type="time" value={s.morning_open} onChange={(e) => updateDaySchedule(day, 'morning_open', e.target.value)} className="text-sm h-8" />
                      <span className="text-xs text-muted-foreground">-</span>
                      <Input type="time" value={s.morning_close} onChange={(e) => updateDaySchedule(day, 'morning_close', e.target.value)} className="text-sm h-8" />
                    </div>
                  </div>
                )}

                {s.type === 'afternoon_only' && (
                  <div className="pl-0 sm:pl-[92px]">
                    <p className="text-xs text-muted-foreground font-medium mb-1">Pomeriggio</p>
                    <div className="flex gap-1 items-center max-w-xs">
                      <Input type="time" value={s.afternoon_open} onChange={(e) => updateDaySchedule(day, 'afternoon_open', e.target.value)} className="text-sm h-8" />
                      <span className="text-xs text-muted-foreground">-</span>
                      <Input type="time" value={s.afternoon_close} onChange={(e) => updateDaySchedule(day, 'afternoon_close', e.target.value)} className="text-sm h-8" />
                    </div>
                  </div>
                )}

                {s.type === 'continuous' && (
                  <div className="pl-0 sm:pl-[92px]">
                    <p className="text-xs text-muted-foreground font-medium mb-1">Orario Continuato</p>
                    <div className="flex gap-1 items-center max-w-xs">
                      <Input type="time" value={s.continuous_open} onChange={(e) => updateDaySchedule(day, 'continuous_open', e.target.value)} className="text-sm h-8" />
                      <span className="text-xs text-muted-foreground">-</span>
                      <Input type="time" value={s.continuous_close} onChange={(e) => updateDaySchedule(day, 'continuous_close', e.target.value)} className="text-sm h-8" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-accent text-accent-foreground hover:bg-accent/90 w-full sm:w-auto" data-testid="save-settings-btn">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio...</> : <><Save className="mr-2 h-4 w-4" /> Salva Modifiche</>}
        </Button>
      </div>
    </div>
  );
};

export default AdminSettingsSection;
